import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  DateRangeDropsTypes,
  DateRangeInterface,
  DateRangeLocaleInterface,
  DateRangeOpensTypes,
  DateRangeRangesDefaultInterface,
  INativeDateOutput,
  ScwMatDatepickerReturnInterface,
  ScwMatDatepickerRule,
} from './scw-mat-datepicker.model';
import { Store } from '@ngrx/store';
import { IotDeviceManagementPortalAppState } from '../../../../store/iotDeviceManagementPortalApp.reducer';
import * as moment from 'moment';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { User } from '../../../../store/user/model';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { HelperService } from '../../../service/helper.service';
import { mysqlDateFormat } from '../../../helper/date';
import { DateRanges } from '../../../../../constants';

@Component({
  selector: 'scw-mat-datepicker',
  templateUrl: './scw-mat-datepicker.component.html',
  styleUrls: ['./scw-mat-datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ScwMatDatepickerComponent implements DateRangeInterface, OnInit, OnDestroy, OnChanges {
  @ViewChild(DaterangepickerDirective) pickerDirective: DaterangepickerDirective;

  @Input() alwaysShowCalendars: boolean = true;
  @Input() autoApply: boolean = false;
  @Input() className: string = '';
  @Input() closeOnAutoApply: boolean = false;
  @Input() customRangeDirection: boolean = false;
  @Input() customStyle = null;
  @Input() disabled: boolean = false;
  @Input() drops: DateRangeDropsTypes = 'down';
  @Input() emptyWeekRowClass: string = '';
  @Input() firstDayOfNextMonthClass: string = '';
  @Input() firstMonthDayClass: string = '';
  @Input() id: string = '';
  @Input() inputModel: ScwMatDatepickerReturnInterface;
  @Input() isCustomDate: () => boolean = () => {
    return false;
  };
  @Input() isInvalidDate: () => boolean = () => {
    return false;
  };
  @Input() keepCalendarOpeningWithRange: boolean = true;
  @Input() lastDayOfPreviousMonthClass: string = '';
  @Input() lastMonthDayClass: string = '';
  @Input() linkedCalendars;
  @Input() locale;
  @Input() lockStartDate: boolean = false;
  @Input() maxDate: moment.Moment = null;
  @Input() minDate: moment.Moment = null;
  @Input() opens: DateRangeOpensTypes = DateRangeOpensTypes.right;
  @Input() placeholder: string = '';
  @Input() hint: string = null;
  @Input() ranges: any;
  @Input() readonly: boolean = true;
  @Input() showCalendarIcon: boolean = true;
  @Input() showCancel: boolean = true;
  @Input() showClearButton: boolean = true;
  @Input() showCustomRangeLabel: boolean = true;
  @Input() showISOWeekNumbers: boolean = false;
  @Input() showRangeLabelOnInput: boolean = false;
  @Input() showWeekNumbers: boolean = true;
  @Input() singleDatePicker: boolean = false;
  @Input() timePicker: boolean = false;
  @Input() timePickerIncrement: number = 1;
  @Input() timePickerSeconds: boolean = false;
  @Input() emitOnNull: boolean = false;
  @Input() errorText: string;
  @Input() isValid: boolean = false;
  @Input() hasErrors: boolean = false;
  @Input() rules: ScwMatDatepickerRule[] = [];
  @Input() label: string;
  @Input() wide: boolean;
  @Input() noPadding: boolean = false;
  @Input() emitUtc: boolean = false;
  @Input() containerClass: string = '';
  @Input() rangePickerStartLabel: string;
  @Input() rangePickerEndLabel: string;

  @Output() inputModelChange: EventEmitter<ScwMatDatepickerReturnInterface> =
    new EventEmitter<ScwMatDatepickerReturnInterface>();
  @Output() isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public timePicker24Hour: boolean = true;
  public locale$: string;
  public dateFormat$: string;
  public dateTimeFormat$: string;
  public nativeInputModel: INativeDateOutput = {
    startDate: null,
    endDate: null,
  };
  public nativeFormattedStartDate: string = '';
  public nativeFormattedEndDate: string = '';
  private isAnyError: boolean = false;
  private firstTime: boolean;
  private userSubscription: Subscription;
  private preventInternalOnChangesOnTouchDevice: boolean;

  constructor(
    private readonly store: Store<IotDeviceManagementPortalAppState>,
    public readonly helperService: HelperService,
  ) {}

  public getDefaultRanges(): DateRanges {
    const rangeTimes: DateRangeRangesDefaultInterface = {
      today: [moment(), moment()],
      yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
      thisWeek: [moment().startOf('week'), moment().endOf('week')],
      lastWeek: [moment().subtract(1, 'weeks').startOf('week'), moment().subtract(1, 'weeks').endOf('week')],
      thisMonth: [moment().startOf('month'), moment().endOf('month')],
      lastMonth: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
      thisYear: [moment().startOf('year'), moment().endOf('year')],
      lastYear: [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
    };

    return this.helperService.getDefaultRanges(rangeTimes);
  }

  public onClickIcon(): void {
    if (this.disabled) {
      return;
    }

    setTimeout(() => {
      this.pickerDirective.open();
    });
  }

  public onClickClear(): void {
    this.inputModelChange.emit(null);
  }

  public ngOnInit(): void {
    this.firstTime = true;
    this.userSubscription = this.store
      .select('user')
      .pipe(take(1))
      .subscribe((state: User) => {
        this.locale$ = 'en';
        this.dateFormat$ = state.dateFormat;
        this.dateTimeFormat$ = state.dateTimeFormat;
        const displayFormatValue: string = this.timePicker ? this.dateTimeFormat$ : this.dateFormat$;

        const defaultLocale: DateRangeLocaleInterface = {
          applyLabel: 'Apply',
          cancelLabel: 'Cancel',
          clearLabel: 'Clear',
          customRangeLabel: 'Custom Range',
          daysOfWeek: moment.localeData(this.locale$).weekdaysShort(),
          direction: 'ltr',
          firstDay: moment.localeData(this.locale$).firstDayOfWeek(),
          format: displayFormatValue,
          monthNames: moment.localeData(this.locale$).monthsShort(),
          separator: ' - ',
          weekLabel: 'Week',
        };

        this.locale = { ...this.locale, ...defaultLocale };

        if (typeof this.ranges === 'undefined') {
          this.ranges = this.getDefaultRanges();
        }
      });

    if (!_.isEmpty(this.inputModel) && !_.isNil(this.inputModel.startDate) && !_.isNil(this.inputModel.endDate)) {
      if (this.emitUtc) {
        this.inputModel = {
          startDate: moment(moment.utc(this.inputModel.startDate).valueOf()),
          endDate: moment(moment.utc(this.inputModel.endDate).valueOf()),
        };
      } else {
        this.inputModel = {
          startDate: moment(this.inputModel.startDate),
          endDate: moment(this.inputModel.endDate),
        };
      }
    }

    if (this.helperService.isTouchDevice) {
      this.equalizeInputModels();
      this.formatNativeDates();
    }
  }

  public ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  public reset(): void {
    this.inputModel = null;
    this.clearErrorMessage();
  }

  private isValidEqualizer(isValid: boolean): void {
    this.isValid = isValid;
    this.isValidChange.emit(this.isValid);
  }

  private showErrorMessage(message: string): void {
    this.isValidEqualizer(false);
    this.isAnyError = true;
    this.hasErrors = true;
    this.errorText = message ? message : '';
  }

  private clearErrorMessage(): void {
    this.isValidEqualizer(true);
    this.isAnyError = false;
    this.hasErrors = false;
    this.errorText = null;
  }

  private requiredRule(rule: ScwMatDatepickerRule): void {
    if (
      this.inputModel === null ||
      (this.inputModel.startDate === null && (!this.singleDatePicker || this.singleDatePicker)) ||
      this.inputModel.endDate === null
    ) {
      this.showErrorMessage(rule.message ?? ('scwMatForm.validation.required'));
    }
  }

  private checkRules(): void {
    if (this.rules.length === 0) {
      this.isValidEqualizer(true);
      return;
    }

    this.isAnyError = false;

    for (const rule of this.rules) {
      if (this.isAnyError) {
        return;
      }

      if ('required' in rule) {
        this.requiredRule(rule);
      }
    }

    if (this.isAnyError) {
      return;
    }

    this.clearErrorMessage();
  }

  private dateStringToMoment(inputModel: ScwMatDatepickerReturnInterface): ScwMatDatepickerReturnInterface {
    return {
      startDate: moment(_.get(inputModel, 'startDate', null)),
      endDate: moment(_.get(inputModel, 'endDate', null)),
    };
  }

  private equalizeInputModels(): void {
    const momentInputModel: ScwMatDatepickerReturnInterface = this.dateStringToMoment(this.inputModel);

    if (
      !this.helperService.isTouchDevice &&
      momentInputModel.startDate.isValid() &&
      momentInputModel.endDate.isValid()
    ) {
      this.inputModel.startDate = momentInputModel.startDate;
      this.inputModel.endDate = momentInputModel.startDate;
    } else {
      if (momentInputModel.startDate.isValid()) {
        this.nativeInputModel.startDate = momentInputModel.startDate.format(
          this.timePicker ? 'YYYY-MM-DDTHH:mm:ssZ' : mysqlDateFormat,
        );
      }

      if (momentInputModel.endDate.isValid()) {
        this.nativeInputModel.endDate = momentInputModel.endDate.format(
          this.timePicker ? 'YYYY-MM-DDTHH:mm:ssZ' : mysqlDateFormat,
        );
      }
    }
  }

  private getFormattedDate(date: string | moment.Moment): string {
    const momentDate: moment.Moment = moment(date);
    momentDate.locale(this.locale$);

    if (!momentDate.isValid()) {
      return '';
    }

    return momentDate.format(this.timePicker ? this.dateTimeFormat$ : this.dateFormat$);
  }

  private formatNativeDates(): void {
    this.nativeFormattedStartDate = this.getFormattedDate(this.nativeInputModel.startDate);
    this.nativeFormattedEndDate = this.getFormattedDate(this.nativeInputModel.endDate);
  }

  private setInputModelBoundaries(): void {
    if (
      this.timePicker ||
      this.inputModel === null ||
      this.inputModel.startDate === null ||
      this.inputModel.endDate === null
    ) {
      return;
    }

    this.inputModel.startDate.set('hours', 0).set('minutes', 0).set('seconds', 0);
    this.inputModel.endDate.set('hours', 23).set('minutes', 59).set('seconds', 59);
  }

  public onNgModelChange(preventInternalOnChangesOnTouchDevice?: boolean): void {
    if (this.helperService.isTouchDevice) {
      if (preventInternalOnChangesOnTouchDevice) {
        this.preventInternalOnChangesOnTouchDevice = true;
      }

      const startDate: moment.Moment =
        _.isEmpty(this.nativeInputModel.startDate) ? null : moment(this.nativeInputModel.startDate);
      const endDate: moment.Moment = this.singleDatePicker
        ? startDate
        : _.isEmpty(this.nativeInputModel.endDate)
        ? null
        : moment(this.nativeInputModel.endDate);

      this.inputModel = {
        startDate,
        endDate,
      };

      this.formatNativeDates();
    } else {
      if (this.firstTime) {
        this.firstTime = false;
        return;
      }
    }

    this.checkRules();
    this.setInputModelBoundaries();

    const isStartEndDateNull: boolean = _.isNil(this.inputModel.startDate) && _.isNil(this.inputModel.endDate);

    if (this.emitUtc && !isStartEndDateNull) {
      this.inputModel.startDate = moment.utc(this.inputModel.startDate);
      this.inputModel.endDate = moment.utc(this.inputModel.endDate);
    }

    this.inputModelChange.emit(!isStartEndDateNull ? this.inputModel : null);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'inputModel' in changes &&
      !_.isEqual(changes['inputModel'].currentValue, changes['inputModel'].previousValue) &&
      this.helperService.isTouchDevice
    ) {
      if (this.preventInternalOnChangesOnTouchDevice) {
        this.preventInternalOnChangesOnTouchDevice = false;
      } else {
        this.equalizeInputModels();
        this.onNgModelChange(true);
      }
    }
  }
}
